/**
 * Environment utility functions
 * Provides helper functions to check environment settings and feature flags
 */

/**
 * Checks if development features should be shown
 * This includes debug tools, mock data toggles, and development credentials
 * @returns boolean indicating if dev features should be visible
 */
export const isDevFeaturesEnabled = (): boolean => {
  return import.meta.env.VITE_SHOW_DEV_FEATURES === "true";
};

/**
 * Checks if the app is running in development mode
 * @returns boolean indicating if in development mode
 */
export const isDevelopment = (): boolean => {
  return import.meta.env.DEV;
};

/**
 * Checks if the app is running in production mode
 * @returns boolean indicating if in production mode
 */
export const isProduction = (): boolean => {
  return import.meta.env.PROD;
};

/**
 * Gets the current environment name
 * @returns string representing the current environment
 */
export const getEnvironment = (): string => {
  return import.meta.env.MODE;
};

/**
 * Gets the API base URL from environment
 * @returns string containing the API base URL
 * @throws Error if VITE_API_BASE_URL is not configured
 */
export const getApiBaseUrl = (): string => {
  const url = import.meta.env.VITE_API_BASE_URL;
  if (!url) {
    throw new Error("VITE_API_BASE_URL environment variable is not configured");
  }
  return url;
};

/**
 * Gets the app name from environment
 * @returns string containing the app name
 */
export const getAppName = (): string => {
  return import.meta.env.VITE_APP_NAME || "AlphaCore Case Management";
};

/**
 * Checks if Stripe payments are enabled
 * @returns boolean indicating if Stripe payments are enabled
 */
export const isStripePaymentsEnabled = (): boolean => {
  return import.meta.env.VITE_ENABLE_STRIPE_PAYMENTS === "true";
};

/**
 * Gets the Stripe publishable key from environment
 * @returns string containing the Stripe publishable key
 */
export const getStripePublishableKey = (): string => {
  return import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";
};

/**
 * Checks if a specific feature is enabled
 * @param featureName - Name of the feature to check
 * @returns boolean indicating if the feature is enabled
 */
export const isFeatureEnabled = (featureName: string): boolean => {
  const envVar = `VITE_ENABLE_${featureName.toUpperCase()}`;
  return import.meta.env[envVar] === "true";
};

/**
 * Gets all environment variables that start with VITE_
 * Useful for debugging environment configuration
 * @returns object containing all VITE_ environment variables
 */
export const getViteEnvVars = (): Record<string, string> => {
  const viteVars: Record<string, string> = {};

  for (const [key, value] of Object.entries(import.meta.env)) {
    if (key.startsWith("VITE_")) {
      viteVars[key] = String(value);
    }
  }

  return viteVars;
};

// Export environment info for debugging
export const environmentInfo = {
  isDev: isDevelopment(),
  isProd: isProduction(),
  mode: getEnvironment(),
  showDevFeatures: isDevFeaturesEnabled(),
  apiBaseUrl: getApiBaseUrl(),
  appName: getAppName(),
};
